import { maxAttachmentCount } from "api/constants";
import { EAttachmentType } from "api/enums";
import AttachmentModel from "api/models/AttachmentModel";
import ContractModel from "api/models/ContractModel";
import AttachmentService from "api/services/AttachmentService";
import ContractService from "api/services/ContractService";
import { lang } from "i18nLang";
import { makeAutoObservable } from "mobx";
import { ContractStore, MessageStore } from "..";

class Store {
  public attachments: AttachmentModel[] = [];
  private attachmentsToExclude: AttachmentModel[] = [];

  constructor() {
    makeAutoObservable(this);
  }

  public reset() {
    this.attachments = [];
    this.attachmentsToExclude = [];
  }

  public async getContractAttachments() {
    this.reset();

    const contract = ContractStore.getCurrentContract();
    if (!contract) return;

    const attachments = await AttachmentService.getAttachmentsByIds(
      contract.uid,
      contract.attachments
    );
    this.attachments = attachments;
  }

  public hasFileBeingUploaded() {
    return this.attachments.some((att) => att.isUploading());
  }

  public validadeAttachmentsCount(files: File[]) {
    return files.length + this.attachments.length <= maxAttachmentCount;
  }

  public async uploadContractAttachment(file: File) {
    try {
      const contractId = ContractStore.getCurrentOrCreatedContractId();
      if (!contractId) return;

      await AttachmentService.validateAttachment(file);

      const attachment = AttachmentService.uploadAttachmentToStorage(
        contractId,
        file,
        EAttachmentType.CONTRACT,
        () => {
          MessageStore.error(lang("attachments.errors.uploadError"));
        }
      );
      this.addAttachmentToList(attachment);
    } catch (error: any) {
      MessageStore.error(error.message);
    }
  }

  public async removeAllUnsavedContractAttachment() {
    const promises: Promise<void>[] = [];
    const attachments = [...this.attachments];
    for (const attachment of attachments) {
      if (!attachment.isSaved()) {
        promises.push(this.removeContractAttachment(attachment));
      }
    }
    await Promise.all(promises);
  }

  public async removeContractAttachment(attachment: AttachmentModel) {
    const contractId = ContractStore.getCurrentOrCreatedContractId();
    if (!contractId) return;

    if (attachment.isSaved()) {
      this.removeAttachmentFromList(attachment);
      this.attachmentsToExclude.push(attachment);
      return;
    }

    if (attachment.isUploading()) {
      attachment.cancelUpload();
      this.removeAttachmentFromList(attachment);
      return;
    }

    return AttachmentService.removeAttachmentFromStorage(contractId, attachment)
      .then(() => {
        this.removeAttachmentFromList(attachment);
      })
      .catch(() => {
        MessageStore.error(lang("attachments.errors.removeError"));
      });
  }

  public async saveContractAttachments(): Promise<void> {
    let contract = ContractStore.getCurrentContract();
    if (!contract) {
      const contractId = ContractStore.getCurrentOrCreatedContractId();
      if (!contractId) return;
      contract = ContractModel.create(contractId, {});
    }

    const attachments = this.attachments.filter((att) =>
      att.isSuccessfullyUploaded()
    );

    await AttachmentService.applyAttachments(
      contract,
      attachments,
      this.attachmentsToExclude,
      (attUids, transaction) =>
        ContractService.updateAttachments(contract!, attUids, transaction)
    ).catch(() => {
      MessageStore.error(lang("attachments.errors.saveError"));
    });
  }

  private addAttachmentToList(attachment: AttachmentModel) {
    this.attachments.push(makeAutoObservable(attachment));
  }

  private removeAttachmentFromList(attachment: AttachmentModel) {
    const index = this.attachments.indexOf(attachment);
    if (index !== -1) {
      this.attachments.splice(index, 1);
    }
  }
}

const ContractAttachmentStore = new Store();
export default ContractAttachmentStore;
